/* eslint-disable react/no-deprecated */
import React from 'react'
import ReactDOM from 'react-dom'
import { Auth0Provider } from '@auth0/auth0-react'
import { graphql, rest } from 'msw'
import 'parkdepot-shared/fonts/pkd/index.css'
import 'parkdepot-shared/fonts/wemolo/index.css'
import { I18nextProvider } from 'react-i18next'
import App from './App'
import i18n from './i18n'
import concatProviders from 'parkdepot-shared/utils/concatProviders'
import createWorker from '../mocks/browser'
import { BookingConsentContainer } from './context/ConsentProvider/ConsentProvider'
import UsercentricsContainer from './context/UsercentricsContainer'
import { Router } from 'react-router-dom'
import { AuthProvider } from './context/AuthContext/AuthContext'
import { ApolloProvider } from '@apollo/client'
import { CheckoutContextContainer } from './context/CheckoutContext/CheckoutContextContainer.context'
import { LotContextContainer } from './context/LotContext/LotContextContainer.context'
import { generateClient } from './apollo-client'
import { ThemeProvider } from 'parkdepot-shared/theme'
import { Client as Styletron } from 'styletron-engine-atomic'
import { SESSION_STORAGE_emptyCarpayPaymentFlow } from '@sessionStorage/carpayPaymentFlow.sessionStorage'
import { browserHistory } from '@utils/browserHistory.utils'
import { isInsidePaymentFlow } from '@utils/isInsidePaymentFlow.utils'
import { clearSavedPaidOrders } from '@utils/carpaySavedPaidOrders.utils'
import CarpayPaymentFlowContainer from '@context/CarpayPaymentFlow/CarpayPaymentContainer'
import { GuestContextContainer } from '@views/Guest/Guest.context'

import './index.css'
import { CarpayErrorFlowContainer } from '@context/ErrorFlowContext/ErrorFlowContextContainer.context'
import { HelmetProvider } from 'react-helmet-async'

async function prepare() {
    if (process.env.VITE_MOCKS === 'true') {
        const worker = createWorker()
        worker.start()
        worker.printHandlers()
        window.msw = { worker, rest, graphql }
    }

    return Promise.resolve()
}

const createApolloClient = async () => {
    const client = await generateClient()
    window.apolloClient = client
    return client
}

// We clean the carpay payment flow if we are refreshing the page in a url which does not belong to the payment flow
// Check useCarpayPaymentFlow
const clearCarpayPaymentFlowData = () => {
    const currentPath = window.location.pathname.split('/').pop() ?? ''
    if (!isInsidePaymentFlow(currentPath)) {
        SESSION_STORAGE_emptyCarpayPaymentFlow()
    }
}

function runApp() {
    prepare()
        .then(createApolloClient)
        .then((client) => {
            console.log('Apollo client initialized:', !!client)

            const RootProviders = concatProviders([
                {
                    Component: UsercentricsContainer,
                    props: { settingsID: process.env.VITE_USERCENTRICS_SETTINGS_ID }
                },
                {
                    Component: ApolloProvider,
                    props: { client }
                },
                {
                    Component: BookingConsentContainer
                },
                {
                    Component: HelmetProvider
                },
                {
                    Component: Auth0Provider,
                    props: {
                        domain: process.env.VITE_AUTH0_DOMAIN || '',
                        clientId: process.env.VITE_AUTH0_CLIENT_ID || '',
                        skipRedirectCallback: true
                    }
                },
                {
                    Component: I18nextProvider,
                    props: { i18n }
                },
                {
                    Component: ThemeProvider,
                    props: {
                        children: null,
                        styletronConfiguration: new Styletron({
                            prefix: 'go_'
                        })
                    }
                },
                {
                    Component: Router,
                    props: { history: browserHistory }
                },
                {
                    Component: AuthProvider
                },
                {
                    Component: LotContextContainer
                },
                {
                    Component: CheckoutContextContainer
                },
                {
                    Component: CarpayErrorFlowContainer
                },
                {
                    Component: CarpayPaymentFlowContainer
                },
                {
                    Component: GuestContextContainer
                }
            ])

            clearCarpayPaymentFlowData()
            clearSavedPaidOrders()

            ReactDOM.render(
                <React.StrictMode>
                    <RootProviders>
                        <App />
                    </RootProviders>
                </React.StrictMode>,
                document.getElementById('root')
            )
        })
        .catch((error) => {
            console.error('App initialization failed:', error)
            throw error
        })
}

runApp()
